import * as THREE from "three";
import type { Block, Blocks } from "../blocks";
import type { Mode } from "../input/keys";
import { applyPush } from "../physics/push";
import type { CameraRig } from "../render/camera";

/* =====================================================================
   PUSH AND REMOVE TOOLS
   A raycast from the pointer finds which block is under it.
   ===================================================================== */
export interface Hit {
  block: Block;
  point: THREE.Vector3;
  dir: THREE.Vector3;
}

export function createActTool(
  blocks: Blocks,
  rig: CameraRig,
  canvas: HTMLCanvasElement,
  state: () => { mode: Mode; grabbing: boolean },
) {
  let hovered: Block | null = null;

  // Outline drawn around the block under the pointer
  const highlight = new THREE.LineSegments(
    new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1)),
    new THREE.LineBasicMaterial({ color: 0xffffff, depthTest: false, transparent: true }),
  );
  highlight.renderOrder = 10;

  function setHovered(b: Block | null) {
    if (hovered === b) return;
    hovered = b;
    const { mode, grabbing } = state();
    if (highlight.parent) highlight.parent.remove(highlight);
    if (b) {
      highlight.scale.set(b.size[0] * 1.03, b.size[1] * 1.03, b.size[2] * 1.03);
      highlight.material.color.set(mode === "remove" ? 0xff5a4f : 0xffffff);
      b.mesh.add(highlight);
    }
    canvas.style.cursor = grabbing
      ? "grabbing"
      : b
        ? mode === "push" || mode === "grab"
          ? "grab"
          : "pointer"
        : "";
  }

  return {
    setHovered,

    pick(x: number, y: number): Hit | null {
      const raycaster = rig.raycast(x, y);
      const hits = raycaster.intersectObjects(
        blocks.list.map((b) => b.mesh),
        false,
      );
      if (!hits.length) return null;
      return {
        block: hits[0].object.userData.block as Block,
        point: hits[0].point,
        dir: raycaster.ray.direction.clone(),
      };
    },

    /** Pushes or removes the hit block, depending on the current mode. */
    act(hit: Hit | null, pushSpeed: number) {
      if (!hit) return;
      const b = hit.block;
      const { mode } = state();
      blocks.wakeAll();
      if (mode === "remove") {
        if (blocks.list.includes(b)) {
          if (hovered === b) setHovered(null);
          blocks.remove(b);
        }
      } else if (mode === "push") {
        applyPush(b.body, hit.dir, hit.point, pushSpeed);
      }
    },
  };
}
